import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useRef, useState, useEffect } from 'react';
import { isButton } from 'ariakit-utils/dom';
import { useTagName, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useCommand } from '../command/command.js';

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native button, the hook will
 * return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/button
 * @example
 * ```jsx
 * const props = useButton({ as: "div" });
 * <Role {...props}>Accessible button</Role>
 * ```
 */

var useButton = createHook(function (props) {
  var ref = useRef(null);
  var tagName = useTagName(ref, props.as || "button");
  var _useState = useState(function () {
      return !!tagName && isButton({
        tagName: tagName,
        type: props.type
      });
    }),
    _useState2 = _slicedToArray(_useState, 2),
    isNativeButton = _useState2[0],
    setIsNativeButton = _useState2[1];
  useEffect(function () {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  props = _objectSpread(_objectSpread({
    role: !isNativeButton && tagName !== "a" ? "button" : undefined
  }, props), {}, {
    ref: useForkRef(ref, props.ref)
  });
  props = useCommand(props);
  return props;
});
/**
 * A component that renders a native accessible button. If another element is
 * passed to the `as` prop, this component will make sure the rendered element
 * is accessible.
 * @see https://ariakit.org/components/button
 * @example
 * ```jsx
 * <Button as="div">Accessible button</Button>
 * ```
 */

var Button = createComponent(function (props) {
  var htmlProps = useButton(props);
  return createElement("button", htmlProps);
});
if (process.env.NODE_ENV !== "production") {
  Button.displayName = "Button";
}
export { Button, useButton };